import React, { useState, useRef, useEffect } from 'react';
import { ShieldCheck, Loader2, X, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

interface TwoFactorModalProps {
  isOpen: boolean;
  onClose: () => void;
  tempToken: string;
  emailHint: string;
  onVerified: (data: any) => void;
  isDeviceVerification?: boolean;
}

export default function TwoFactorModal({ isOpen, onClose, tempToken, emailHint, onVerified, isDeviceVerification }: TwoFactorModalProps) {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [trustDevice, setTrustDevice] = useState(true);
  const [currentToken, setCurrentToken] = useState(tempToken);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const prevOpenRef = useRef(false);

  useEffect(() => { setCurrentToken(tempToken); }, [tempToken]);

  // Only reset form when modal OPENS (false → true transition), not on prop changes
  useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      setCode(['', '', '', '', '', '']);
      setTrustDevice(true);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
    prevOpenRef.current = isOpen;
  }, [isOpen]);

  const getDeviceId = (): string => {
    let id = localStorage.getItem('device_id');
    if (!id) {
      id = 'dev_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
      localStorage.setItem('device_id', id);
    }
    return id;
  };

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length > 0) {
      const newCode = [...code];
      for (let i = 0; i < 6; i++) {
        newCode[i] = pasted[i] || '';
      }
      setCode(newCode);
      const focusIdx = Math.min(pasted.length, 5);
      inputRefs.current[focusIdx]?.focus();
    }
  };

  const handleVerify = async () => {
    const codeStr = code.join('');
    if (codeStr.length !== 6) {
      toast.error('Please enter the full 6-digit code');
      return;
    }
    setVerifying(true);
    try {
      const deviceId = getDeviceId();
      const ua = navigator.userAgent;
      let deviceName = 'Unknown Device';
      if (/iPhone|iPad/.test(ua)) deviceName = 'iPhone/iPad';
      else if (/Android/.test(ua)) deviceName = 'Android';
      else if (/Mac/.test(ua)) deviceName = 'Mac';
      else if (/Windows/.test(ua)) deviceName = 'Windows PC';
      else if (/Linux/.test(ua)) deviceName = 'Linux';

      const resp = await fetch('/api/auth/2fa/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          code: codeStr,
          temp_token: currentToken,
          trust_device: trustDevice,
          device_id: deviceId,
          device_name: deviceName,
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Verification failed');
      }
      const data = await resp.json();
      toast.success('Verified successfully!');
      onVerified(data);
    } catch (err: any) {
      toast.error(err.message || 'Invalid code');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const resp = await fetch('/api/auth/2fa/resend', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`,
        },
      });
      if (!resp.ok) throw new Error('Failed to resend');
      const data = await resp.json();
      if (data.temp_token) setCurrentToken(data.temp_token);
      toast.success('New code sent to your email');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } catch {
      toast.error('Failed to resend code. Try logging in again.');
    } finally {
      setResending(false);
    }
  };

  // Do NOT auto-submit — let user check "Trust this device" before clicking Verify

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4">
      <div className="bg-[#1b2028] border border-[#20262f] rounded-2xl w-full max-w-sm p-6 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#a8abb3] hover:text-white" data-testid="2fa-close-btn">
          <X className="h-5 w-5" />
        </button>

        <div className="text-center mb-6">
          <div className={`w-14 h-14 ${isDeviceVerification ? 'bg-yellow-500/20' : 'bg-orange-500/20'} rounded-full flex items-center justify-center mx-auto mb-3`}>
            <ShieldCheck className={`h-7 w-7 ${isDeviceVerification ? 'text-yellow-400' : 'text-orange-400'}`} />
          </div>
          <h2 className="text-xl font-bold text-white">
            {isDeviceVerification ? 'New Device Detected' : 'Verify Your Identity'}
          </h2>
          <p className="text-sm text-[#a8abb3] mt-1">
            {isDeviceVerification ? (
              <>This device is not yet trusted. A verification code was sent to <span className="text-slate-200">{emailHint}</span>. Enter it to trust this device.</>
            ) : (
              <>Enter the 6-digit code sent to <span className="text-slate-200">{emailHint}</span></>
            )}
          </p>
        </div>

        {/* Code Input */}
        <div className="flex gap-2 justify-center mb-5" onPaste={handlePaste} data-testid="2fa-code-inputs">
          {code.map((digit, i) => (
            <input
              key={i}
              ref={(el) => { inputRefs.current[i] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              className="w-11 h-13 text-center text-2xl font-bold bg-[#20262f] border border-[#20262f] rounded-lg text-white focus:border-orange-400 focus:ring-1 focus:ring-orange-400 outline-none transition-colors"
              data-testid={`2fa-digit-${i}`}
            />
          ))}
        </div>

        {/* Trust Device */}
        <label className="flex items-center gap-2.5 mb-5 cursor-pointer px-1" data-testid="trust-device-checkbox">
          <input
            type="checkbox"
            checked={trustDevice}
            onChange={(e) => setTrustDevice(e.target.checked)}
            className="w-4 h-4 rounded border-[#20262f] bg-[#20262f] text-orange-500 focus:ring-orange-400"
          />
          <span className="text-sm text-[#f1f3fc]">Trust this device for 30 days</span>
        </label>

        {/* Verify Button */}
        <Button
          onClick={handleVerify}
          disabled={verifying || code.some(d => d === '')}
          className="w-full bg-orange-500 hover:bg-orange-400 text-white font-semibold py-2.5"
          data-testid="2fa-verify-btn"
        >
          {verifying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          {verifying ? 'Verifying...' : 'Verify'}
        </Button>

        {/* Resend */}
        <div className="text-center mt-4">
          <button
            onClick={handleResend}
            disabled={resending}
            className="text-sm text-[#a8abb3] hover:text-orange-400 transition-colors inline-flex items-center gap-1"
            data-testid="2fa-resend-btn"
          >
            {resending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            Resend code
          </button>
        </div>
      </div>
    </div>
  );
}
